/**
 * Usage Gauge Component
 * ====================
 * A semicircular gauge for displaying CPU/RAM usage with color coding.
 * Green (0-60%), Yellow (60-80%), Red (80-100%)
 */

interface UsageGaugeProps {
  value: number | null  // 0-100 percentage
  label: string
  size?: 'sm' | 'md' | 'lg'
}

export default function UsageGauge({ value, label, size = 'md' }: UsageGaugeProps) {
  // Handle null/undefined values
  const displayValue = value ?? 0
  const hasValue = value !== null && value !== undefined

  // Determine color based on value
  const getColor = (val: number): string => {
    if (val >= 80) return '#ef4444' // Red
    if (val >= 60) return '#eab308' // Yellow
    return '#22c55e' // Green
  }

  // Size configurations
  const sizes = {
    sm: { width: 80, height: 50, strokeWidth: 6, fontSize: 14, labelSize: 10 },
    md: { width: 100, height: 60, strokeWidth: 8, fontSize: 18, labelSize: 11 },
    lg: { width: 120, height: 70, strokeWidth: 10, fontSize: 22, labelSize: 12 },
  }

  const config = sizes[size]
  const radius = (config.width - config.strokeWidth) / 2
  const centerX = config.width / 2
  const centerY = config.height - 5

  // Calculate arc path (semicircle from left to right)
  const startAngle = Math.PI
  const endAngle = 0
  const valueAngle = startAngle - ((displayValue / 100) * Math.PI)

  // Arc start point
  const startX = centerX + radius * Math.cos(startAngle)
  const startY = centerY - radius * Math.sin(startAngle)

  // Arc end point for background (full semicircle)
  const bgEndX = centerX + radius * Math.cos(endAngle)
  const bgEndY = centerY - radius * Math.sin(endAngle)

  // Arc end point for value
  const valueEndX = centerX + radius * Math.cos(valueAngle)
  const valueEndY = centerY - radius * Math.sin(valueAngle)

  // SVG arc path (large arc flag is always 0 for less than 180 degrees)
  const largeArcFlag = displayValue > 50 ? 1 : 0

  const bgPath = `M ${startX} ${startY} A ${radius} ${radius} 0 1 1 ${bgEndX} ${bgEndY}`
  const valuePath = displayValue > 0
    ? `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${valueEndX} ${valueEndY}`
    : ''

  const color = getColor(displayValue)

  return (
    <div className="flex flex-col items-center">
      <svg width={config.width} height={config.height} className="overflow-visible">
        {/* Background arc (gray) */}
        <path
          d={bgPath}
          fill="none"
          stroke="currentColor"
          strokeWidth={config.strokeWidth}
          strokeLinecap="round"
          className="text-gray-200 dark:text-gray-700"
        />
        {/* Value arc (colored) */}
        {hasValue && displayValue > 0 && (
          <path
            d={valuePath}
            fill="none"
            stroke={color}
            strokeWidth={config.strokeWidth}
            strokeLinecap="round"
          />
        )}
        {/* Percentage text */}
        <text
          x={centerX}
          y={centerY - 8}
          textAnchor="middle"
          fill={hasValue ? color : 'currentColor'}
          fontSize={config.fontSize}
          fontWeight="bold"
          className={!hasValue ? 'text-gray-400 dark:text-gray-500' : ''}
        >
          {hasValue ? `${displayValue}%` : '-'}
        </text>
      </svg>
      {/* Label below */}
      <span
        className="text-gray-600 dark:text-gray-400 font-medium -mt-1"
        style={{ fontSize: config.labelSize }}
      >
        {label}
      </span>
    </div>
  )
}
